import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Alert,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  AccountCircle as AccountIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTokenValidation } from '../providers/TokenValidationProvider';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: '600px',
    width: '100%',
    margin: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0.5),
      borderRadius: theme.spacing(1),
      maxHeight: '95vh',
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.contrastText,
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const ExpiredAccountItem = styled(ListItem)(({ theme }) => ({
  border: `1px solid ${theme.palette.error.main}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.error.light,
  '&:hover': {
    backgroundColor: theme.palette.error.light,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const PlatformChip = styled(Chip)(({ theme }) => ({
  textTransform: 'capitalize',
  fontWeight: 'bold',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
  },
}));

/**
 * ExpiredAccountsModal displays all expired social media accounts in a single modal
 * 
 * Features:
 * - Mobile-responsive design
 * - Accessibility compliant (WCAG AA)
 * - Shows all expired accounts at once
 * - Provides direct navigation to profile page
 * - Smart dismissal tracking
 */
const ExpiredAccountsModal = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    isModalVisible,
    expiredAccounts,
    dismissModal,
  } = useTokenValidation();

  /**
   * Handle closing the modal with dismissal tracking
   */
  const handleClose = () => {
    dismissModal();
  };

  /**
   * Navigate to profile page and close modal
   */
  const handleGoToProfile = () => {
    navigate('/profile');
    dismissModal();
  };

  /**
   * Get platform display name
   */
  const getPlatformDisplayName = (platform) => {
    const platformNames = {
      twitter: 'Twitter',
      facebook: 'Facebook',
      instagram: 'Instagram',
      linkedin: 'LinkedIn',
      linkedin_org: 'LinkedIn Organization',
      tiktok: 'TikTok',
      threads: 'Threads',
      bluesky: 'BlueSky',
      mastodon: 'Mastodon',
    };
    return platformNames[platform] || platform;
  };

  /**
   * Get platform color for visual distinction
   */
  const getPlatformColor = (platform) => {
    const colors = {
      twitter: '#1DA1F2',
      facebook: '#4267B2',
      instagram: '#E4405F',
      linkedin: '#0077B5',
      linkedin_org: '#0077B5',
      tiktok: '#000000',
      threads: '#000000',
      bluesky: '#00D4FF',
      mastodon: '#595AFF',
    };
    return colors[platform] || theme.palette.primary.main;
  };

  if (!isModalVisible || expiredAccounts.length === 0) {
    return null;
  }

  return (
    <StyledDialog
      open={isModalVisible}
      onClose={handleClose}
      aria-labelledby="expired-accounts-dialog-title"
      aria-describedby="expired-accounts-dialog-description"
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <StyledDialogTitle id="expired-accounts-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon />
          <Typography variant="h6" component="h2">
            Account Reconnection Required
          </Typography>
        </Box>
        <IconButton
          aria-label="Close modal"
          onClick={handleClose}
          size="small"
          sx={{ 
            color: 'inherit',
            minWidth: '44px',
            minHeight: '44px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Box id="expired-accounts-dialog-description">
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          >
            We lost access to {expiredAccounts.length === 1 ? 'this account' : 'these accounts'}. 
            Please reconnect to continue posting to your social media platforms.
          </Alert>

          <Typography 
            variant="subtitle1" 
            fontWeight="bold" 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
          >
            Accounts that need reconnection:
          </Typography>

          <List sx={{ p: 0 }}>
            {expiredAccounts.map((account, index) => (
              <ExpiredAccountItem
                key={`${account.platform}-${account.account_id}-${index}`}
                role="listitem"
              >
                <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
                  <Avatar
                    src={account.account_image || ''}
                    sx={{ 
                      width: { xs: 40, sm: 48 }, 
                      height: { xs: 40, sm: 48 },
                      bgcolor: getPlatformColor(account.platform)
                    }}
                  >
                    <AccountIcon />
                  </Avatar>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {account.username || account.account_name || 'Unknown Account'}
                      </Typography>
                      <PlatformChip
                        label={getPlatformDisplayName(account.platform)}
                        size={isMobile ? 'small' : 'medium'}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mt: 0.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      {account.error_message || 'Authentication expired - reconnection required'}
                    </Typography>
                  }
                />
              </ExpiredAccountItem>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          <Box textAlign="center">
            <Typography 
              variant="body2" 
              color="text.secondary" 
              paragraph
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Go to your profile page to reconnect these accounts. 
              The reconnection process will guide you through re-authorizing each platform.
            </Typography>
          </Box>
        </Box>
      </StyledDialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box 
          display="flex" 
          gap={2} 
          width="100%" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            size={isMobile ? 'large' : 'medium'}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              order: { xs: 2, sm: 1 }
            }}
          >
            Remind Me Later
          </Button>
          
          <Button
            onClick={handleGoToProfile}
            variant="contained"
            color="primary"
            size={isMobile ? 'large' : 'medium'}
            endIcon={<ArrowIcon />}
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              order: { xs: 1, sm: 2 }
            }}
          >
            Go to Profile
          </Button>
        </Box>
      </DialogActions>
    </StyledDialog>
  );
};

export default ExpiredAccountsModal; 